import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'dva';
import { Form, Input, Spin, Row, Col, Card, Button, Icon, Comment, Avatar } from 'antd';
import { CancelToken } from 'axios';
import ButtonBar from '../../ButtonBar/ButtonBar';
import { TrashIcon } from '../../Icons';
import { ACTION_APP_ADD_NOTIFICATION, ACTION_APP_GO_TO_ROUTE } from '../../../models/app';
import {
  ACTION_AI_OUTREACH_AGENTS_CREATE,
  ACTION_AI_OUTREACH_AGENTS_UPDATE,
  ACTION_AI_OUTREACH_AGENTS_LOAD_ITEM,
} from '../../../models/aiOutreachAgents';
import { ACTION_HEADER_UPDATE } from '../../../models/header';
import { ROUTE_AI_OUTREACH_AGENTS_LIST } from '../../../routes';
import styles from './../styles.less';
import { RemoteSelectInput } from '../../index';
import { searchCompanyByNameRequest } from '../../../services/companies';
import * as aiOutreachAgentService from '../../../services/aiOutreachAgents';
import { helpers } from '../../../utils';

const { Item } = Form;
const { TextArea } = Input;

const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
const urlPattern = /^https?:\/\/[^\s\\]*linkedin\.com\/in\/[^\s\\]*\/?$/;

const AIOutreachAgentsItem = (props) => {
  const { loading, form, intl } = props;
  const projectId = props.app.currentProjectId || props.match.params.projectId;
  const copyId = props.match.params.copyAIOutreachAgentId || null;
  const aiOutreachAgentId = props.match.params.aiOutreachAgentId || null;
  const keysField = form.getFieldValue('keys') ? [...form.getFieldValue('keys')] : [];
  const [aiOutreachAgent, setAiOutreachAgent] = useState(null);
  const [promptInfo, setPromptInfo] = useState({ result: '', loading: false });

  useEffect(() => {
    if (aiOutreachAgentId || copyId) {
      const payload = {
        projectId,
        id: aiOutreachAgentId || copyId,
      };
      props
        .dispatch({ type: ACTION_AI_OUTREACH_AGENTS_LOAD_ITEM, payload })
        .then((response) => {
          const urlFields = {};
          response.url_fields.forEach((item, index) => {
            urlFields[`${Date.now()}-${index}`] = item;
          });
          setAiOutreachAgent({
            id: aiOutreachAgentId,
            name: response.name,
            prompt: response.prompt,
            url_fields: urlFields,
          });
          form.setFieldsValue({
            keys: Object.keys(urlFields),
          });
        });
    }

    props.dispatch({
      type: ACTION_HEADER_UPDATE,
      payload: {
        title: aiOutreachAgentId
          ? 'Component.AIOutreachAgents.Edit.Form.Title'
          : 'Component.AIOutreachAgents.New.Form.Title',
      },
    });
  },
  []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const { getFieldsValue, validateFieldsAndScroll } = form;
    validateFieldsAndScroll(['name', 'url_fields', 'prompt'], (err) => {
      if (!err) {
        const { name, url_fields, prompt = null, keys } = getFieldsValue();
        saveAIOutreachAgent({ name, url_fields: keys.map(k => url_fields[k]), prompt });
      }
    });
  };

  const saveAIOutreachAgent = async (data) => {
    const id = aiOutreachAgent ? aiOutreachAgent.id : null;
    const success = await props.dispatch({
      type: id ? ACTION_AI_OUTREACH_AGENTS_UPDATE : ACTION_AI_OUTREACH_AGENTS_CREATE,
      payload: {
        id,
        data,
        projectId,
      },
    });
    if (success) {
      addNotification(id);
      goToAIOutreachAgents();
    }
  };

  const goToAIOutreachAgents = () => {
    props.dispatch({
      type: ACTION_APP_GO_TO_ROUTE,
      payload: { routeName: ROUTE_AI_OUTREACH_AGENTS_LIST, projectId },
    });
  };

  const addNotification = (id) => {
    props.dispatch({
      type: ACTION_APP_ADD_NOTIFICATION,
      payload: {
        type: 'success',
        message: 'Common.Success',
        description: id
          ? 'Component.AIOutreachAgents.Edit.Success.Text'
          : 'Component.AIOutreachAgents.New.Success.Text',
      },
    });
  };

  const newUrlLabelsSet = () => {
    const keys = form.getFieldValue('keys') || [];
    if (keys.length >= 5) { return; }
    form.setFieldsValue({
      keys: [...keys, `${Date.now()}-${keys.length}`],
    });
  };

  const deleteUrlsLabelSet = k => () => {
    const keys = form.getFieldValue('keys');
    form.setFieldsValue({
      keys: keys.filter(key => key !== k),
    });
  };

  const companiesLoader = () => {
    return (query) => {
      let cancel;

      const requestConfig = {
        cancelToken: new CancelToken((c) => {
          cancel = c;
        }),
      };

      return {
        request: searchCompanyByNameRequest({ projectId, query }, requestConfig),
        cancel,
      };
    };
  };

  const testPrompt = async (companyId, prompt, urls, email, linkedin_url) => {
    setPromptInfo({ result: '', loading: true });
    try {
      const result = await aiOutreachAgentService.testPrompt(projectId, companyId, prompt, urls, email, linkedin_url);
      setPromptInfo({ result: result.text, loading: false });
    } catch (err) {
      helpers.processException(err);
      setPromptInfo({ result: '', loading: false });
    }
  };

  form.getFieldDecorator('keys', { initialValue: [] });

  return (
    <Spin
      spinning={
        !!loading.effects[ACTION_AI_OUTREACH_AGENTS_CREATE] ||
        !!loading.effects[ACTION_AI_OUTREACH_AGENTS_UPDATE] ||
        !!loading.effects[ACTION_AI_OUTREACH_AGENTS_LOAD_ITEM]
      }
    >
      <div className={styles.containerLarge}>
        <Form onSubmit={handleSubmit}>
          <Card>
            <Row>
              <Col>
                <Item
                  label={
                    <FormattedMessage id="Component.AIOutreachAgents.Form.Name" />
                  }
                >
                  {form.getFieldDecorator('name', {
                    initialValue: aiOutreachAgent ? aiOutreachAgent.name : '',
                    rules: [
                      {
                        required: true,
                        min: 3,
                        max: 255,
                        message: intl.formatMessage({
                          id: 'Component.AIOutreachAgents.Validation.Name',
                        }),
                      },
                    ],
                  })(<Input maxLength={255} type="text" />)}
                </Item>
              </Col>
            </Row>
          </Card>
          <Card
            className="custom-card"
            title={intl.formatMessage({ id: 'Component.AIOutreachAgents.Form.URLLabel.Title' })}
          >
            <Row gutter={12}>
              {keysField.map(k => (
                <Col key={k} xxl={6} xl={8} lg={12} md={24}>
                  <Item
                    label={
                      intl.formatMessage({
                        id: 'Component.AIOutreachAgents.Form.URLLabel.Value',
                      })
                    }
                  >
                    {form.getFieldDecorator(`url_fields[${k}]`, {
                      initialValue: aiOutreachAgent ? aiOutreachAgent.url_fields[k] || '' : '',
                      rules: [
                        {
                          required: true,
                          min: 1,
                          max: 4000,
                          message: intl.formatMessage({
                            id: 'Component.AIOutreachAgents.Validation.URLLabel.Value',
                          }),
                        },
                      ],
                    })(
                      <Input
                        maxLength={1000}
                        addonAfter={
                          <span
                            onClick={deleteUrlsLabelSet(k)}
                            className={styles.delete}
                          >
                            <TrashIcon />
                          </span>
                        }
                        type="text"
                      />,
                    )}
                  </Item>
                </Col>
              ))}
            </Row>

            <Row type="flex" justify="end">
              <Button size="large" type="primary" disabled={keysField.length === 5} onClick={newUrlLabelsSet}>
                <Icon type="plus" />
                <FormattedMessage id="Component.AIOutreachAgents.Form.URLLabel.Add" />
              </Button>
            </Row>
          </Card>
          <Card
            className="custom-card"
            title={intl.formatMessage({ id: 'Component.AIOutreachAgents.Form.Prompt.CardTitle' })}
          >
            <Spin spinning={promptInfo.loading}>
              <FormattedMessage id="Component.AIOutreachAgents.Form.Prompt.PromptDescription1" /><br />
              <FormattedMessage id="Component.AIOutreachAgents.Form.Prompt.PromptDescription2" /><br />
              <FormattedMessage id="Component.AIOutreachAgents.Form.Prompt.PromptDescription3" /><br />
              <Row type={'flex'} gutter={32} style={{ marginTop: '30px' }}>
                <Col span={24}>
                  <Form.Item>
                    {form.getFieldDecorator('prompt', {
                      initialValue: aiOutreachAgent ? aiOutreachAgent.prompt : '',
                      rules: [{
                        required: false,
                      }],
                    })(<
                      TextArea
                      style={{ height: '200px' }}
                      autoSize={{ minRows: 6, maxRows: 10 }}
                    />)}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Item
                    label={
                      <FormattedMessage id="Component.AIOutreachAgents.Form.EmailOrLinkedIn" />
                    }
                  >
                    {form.getFieldDecorator('email_or_linkedin', {
                      initialValue: '',
                      rules: [
                        {
                          message: intl.formatMessage({
                            id: 'Component.AIOutreachAgents.Validation.EmailOrLinkedIn',
                          }),
                          validator: (_, value) => {
                            if (!value || (emailPattern.test(value) || urlPattern.test(value))) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(intl.formatMessage({
                                id: 'Component.AIOutreachAgents.Validation.EmailOrLinkedIn',
                              }));
                            }
                          },
                        },
                      ],
                    })(<Input type="text" />)}
                  </Item>
                </Col>
              </Row>
              <Row gutter={16}>
                {keysField.map(k => (
                  <Col key={k} xxl={6} xl={8} lg={12} md={24}>
                    <Item
                      label={
                        (form.getFieldValue('url_fields') || {})[k] || '-'
                      }
                    >
                      {form.getFieldDecorator(`url_fields_value[${k}]`, {
                        initialValue: '',
                        rules: [
                          {
                            type: 'url',
                            message: intl.formatMessage({
                              id: 'Component.AIOutreachAgents.Validation.URL.Value',
                            }),
                          },
                        ],
                      })(
                        <Input
                          maxLength={4000}
                          type="text"
                        />,
                      )}
                    </Item>
                  </Col>
                ))}
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={<FormattedMessage id="Component.AIOutreachAgents.Form.Prompt.Company.Label" />}
                  >
                    {form.getFieldDecorator('test_company_id', {})(
                      <RemoteSelectInput
                        nameKey="company_name"
                        remoteLoader={companiesLoader()}
                        notFoundContent={intl.formatMessage({ id: 'Component.AIOutreachAgents.Form.Prompt.Company.NotFound' })}
                        placeholder={intl.formatMessage({ id: 'Common.RemoteSelect.Placeholder' })}
                        showArrow
                      />,
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Button
                    disabled={!form.getFieldValue('test_company_id')}
                    type="primary"
                    style={{ marginTop: '48px' }}
                    onClick={() => {
                      form.validateFieldsAndScroll(['url_fields_value', 'email_or_linkedin', 'test_company_id'], (err) => {
                        if (err) {
                          return;
                        }
                        const urlFieldValues = form.getFieldValue('url_fields_value');
                        const emailOrLinkedin = form.getFieldValue('email_or_linkedin');
                        const isEmail = emailPattern.test(emailOrLinkedin);
                        const email = isEmail ? emailOrLinkedin : null;
                        const linkedin_url = isEmail ? null : urlPattern.test(emailOrLinkedin) ? emailOrLinkedin : null;
                        testPrompt(
                          form.getFieldValue('test_company_id'),
                          form.getFieldValue('prompt'),
                          keysField.map(k => urlFieldValues[k]).filter(v => v),
                          email,
                          linkedin_url,
                        );
                      });
                    }}
                  >
                    <FormattedMessage id="Common.Test" />
                  </Button>
                </Col>
              </Row>
              {promptInfo.result &&
              <Row>
                <Comment
                  author="AI Sales Assistant"
                  avatar={<Avatar src="/dist/ai-sales-avatar.jpg" />}
                  content={
                    <p style={{ whiteSpace: 'pre-line' }}>
                      {promptInfo.result}
                    </p>
                  }
                />
              </Row>}
            </Spin>
          </Card>
          <ButtonBar cancelAction={goToAIOutreachAgents} />
        </Form>
      </div>
    </Spin>
  );
};

export default connect(({ app, loading }) => ({ app, loading }))(
  Form.create()(injectIntl(AIOutreachAgentsItem)),
);



// WEBPACK FOOTER //
// ./src/components/AIOutreachAgents/AIOutreachAgentsItem/index.js